import { useMutation } from '@apollo/client';
import type { FormikErrors } from 'formik';
import * as _ from 'lodash-es';
import moment from 'moment';
import React from 'react';
import * as styles from './EditVerificationModal.module.scss';
import getVerificationStatus, {
  VerificationStatus,
} from './getVerificationStatus';
import { calculateExpirationTimestamp } from './verificationDateHelpers';
import type { VerificationFormValues } from './VerificationFrequencyFields';
import VerificationFrequencyFields, {
  FREQUENCIES,
  validateVerificationFrequencyFields,
} from './VerificationFrequencyFields';
import {
  VerificationModalHr,
  VerificationModalNotice,
} from './VerificationModal';
import VerificationVerifiersFields from './VerificationVerifiersFields';
import VerifiedIcon from './VerifiedIcon';
import { useAppDispatch } from '../app/redux/useAppSelector';
import Button from '../components/Button';
import ConfirmationModal from '../components/ConfirmationModal';
import Form, { Submit } from '../components/Form';
import Modal, { ModalActionFooter, ModalTitle } from '../components/Modal';
import SVGIcon from '../components/SVGIcon';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import alertIcon from '../icons/non-prefilled/16x16/alert.svg';
import { UIActions } from '../ui/uiSlice';
import yearSensitiveDateString from '../utils/yearSensitiveDateString';

const UPDATE_VERIFICATION_MUTATION = gql(/** @complexity 12 */ `
  mutation updateVerification(
    $verificationId: ID!
    $unit: VerificationUnit!
    $quantity: Int!
    $primaryVerifierId: ID!
    $secondaryVerifierIds: [ID!]!
  ) {
    verification: updateVerification(
      verificationId: $verificationId
      unit: $unit
      quantity: $quantity
      primaryVerifierId: $primaryVerifierId
      secondaryVerifierIds: $secondaryVerifierIds
    ) {
      id
      post {
        id
        verifiedAt
        verification {
          id
          unit
          quantity
          primaryVerifier {
            id
          }
          secondaryVerifiers {
            id
          }
        }
      }
    }
  }
`);

const DELETE_VERIFICATION_MUTATION = gql(/** @complexity 6 */ `
  mutation deleteVerification($verificationId: ID!) {
    verification: deleteVerification(verificationId: $verificationId) {
      id
      post {
        id
        verifiedAt
        verification {
          id
        }
      }
    }
  }
`);

const POST_FRAGMENT = gql(`
  fragment EditVerificationModal_post on Post {
    id
    verification {
      id
      unit
      quantity
      primaryVerifier {
        id
      }
      secondaryVerifiers {
        id
      }
    }
    # eslint-disable-next-line @graphql-eslint/require-selections
    verificationEntries {
      expiredAt
      verifiedAt
    }
    ...getVerificationStatus_post
    ...VerificationVerifiersFields_post
  }
`);

type FormValues = VerificationFormValues & {
  primaryVerifierId: string;
  secondaryVerifierIds: string[];
  all?: unknown;
};

interface EditVerificationModalProps {
  post: DocumentType<typeof POST_FRAGMENT>;
  close: () => void;
}

const EditVerificationModal = ({ post, close }: EditVerificationModalProps) => {
  const dispatch = useAppDispatch();
  const status = getVerificationStatus(post);
  const isExpired = status === VerificationStatus.Expired;

  let subtitle;
  if (status === VerificationStatus.Requested) {
    subtitle = 'Verification request is pending.';
  } else if (post.verification) {
    const expirationDateString = yearSensitiveDateString(
      isExpired
        ? post.verificationEntries[0]?.expiredAt
        : calculateExpirationTimestamp(
            post.verificationEntries[0]?.verifiedAt,
            post.verification,
          ).unsafe(),
    );

    if (isExpired) {
      subtitle = `Verification expired on ${expirationDateString}.`;
    } else {
      subtitle = `Verification is set to expire on ${expirationDateString}. Changing the frequency will also change its expiration date.`;
    }
  }

  const initialValues: FormValues = {
    ...(FREQUENCIES.find(
      ({ unit, quantity }) =>
        unit === post.verification?.unit &&
        quantity === post.verification.quantity,
    ) ?? {
      frequency: 'Custom',
      unit: post.verification?.unit ?? null,
      quantity: post.verification?.quantity ?? null,
    }),
    primaryVerifierId: post.verification?.primaryVerifier?.id ?? '',
    secondaryVerifierIds: _.compact(
      post.verification?.secondaryVerifiers.map((verifier) => verifier?.id),
    ),
  };

  const validate = (values: FormValues) => {
    const errors: FormikErrors<FormValues> = {
      ...validateVerificationFrequencyFields(values),
    };
    if (Object.keys(errors).length > 0) {
      return errors;
    }
    const { unit, quantity } = values;
    const expirationTimestamp =
      unit && quantity
        ? calculateExpirationTimestamp(
            post.verificationEntries[0]?.verifiedAt,
            { unit, quantity },
          ).getOrElse(() => null)
        : null;

    if (expirationTimestamp == null) {
      errors.quantity = 'Invalid quantity';
      return errors;
    }

    if (
      !isExpired &&
      post.verificationEntries.length > 0 &&
      moment(expirationTimestamp) <= moment()
    ) {
      errors.all =
        'At this frequency, the verification would have already expired. Choose a frequency that will expire in the future.';
    }
    return errors;
  };

  const [updateVerification] = useMutation(UPDATE_VERIFICATION_MUTATION);
  const onSubmit = async ({
    unit,
    quantity,
    primaryVerifierId,
    secondaryVerifierIds,
  }: FormValues) => {
    if (!unit || !post.verification?.id) {
      return;
    }
    await updateVerification({
      variables: {
        unit,
        quantity: Number(quantity),
        verificationId: post.verification.id,
        primaryVerifierId,
        secondaryVerifierIds,
      },
    });
    close();
  };

  const [deleteVerification] = useMutation(DELETE_VERIFICATION_MUTATION);
  const onRemove = () =>
    dispatch(
      UIActions.openModal(ConfirmationModal, {
        title: 'Remove verification?',
        message: 'Are you sure you want to remove the verification?',
        onConfirm: () =>
          deleteVerification({
            variables: {
              // @ts-expect-error
              verificationId: post.verification?.id,
            },
          }),
      }),
    );

  return (
    <Modal width={534}>
      <ModalTitle subtitle={subtitle} close={close}>
        Edit verification <VerifiedIcon inline size="large" />
      </ModalTitle>
      <Form<FormValues>
        onSubmit={onSubmit}
        initialValues={initialValues}
        validate={validate}
      >
        {({ values, setFieldValue, errors, isValid, dirty }) => {
          return (
            <>
              <VerificationVerifiersFields
                post={post}
                primaryVerifierId={values.primaryVerifierId}
                secondaryVerifierIds={values.secondaryVerifierIds}
                setPrimaryVerifier={(id) =>
                  setFieldValue('primaryVerifierId', id)
                }
                setSecondaryVerifierIds={(ids) =>
                  setFieldValue('secondaryVerifierIds', ids)
                }
              />
              <VerificationModalHr />
              <VerificationFrequencyFields
                frequency={values.frequency}
                setFieldValue={setFieldValue}
              />

              {status === VerificationStatus.Verified &&
                (!dirty || isValid) &&
                values.unit &&
                values.quantity != null && (
                  <VerificationModalNotice
                    verifiedAt={post.verificationEntries[0]?.verifiedAt}
                    quantity={values.quantity}
                    unit={values.unit}
                  >
                    {(dateString) =>
                      `We’ll notify the post owner to reverify this post on ${dateString}.`
                    }
                  </VerificationModalNotice>
                )}
              {errors.all && (
                <div className={styles.error}>
                  <SVGIcon className={styles.errorIcon}>{alertIcon}</SVGIcon>
                  {errors.all}
                </div>
              )}
              <ModalActionFooter
                primary={
                  <Submit size="large" disabled={!dirty || !isValid}>
                    Save
                  </Submit>
                }
                secondary={
                  <Button danger onClick={onRemove}>
                    Remove
                  </Button>
                }
              />
            </>
          );
        }}
      </Form>
    </Modal>
  );
};

export default EditVerificationModal;
